import React, { useState, useEffect, useContext, useRef } from "react";
import { Formik, Form, Field } from "formik";
import { cn, isEmail, getStashed } from "../../lib/helpers";
import { Button } from "../button";
import { Dialog } from "./dialog";
import { HiddenFields } from "../web-form-hidden-fields";
import { SiteContext } from "./site-context";
import { clientAttribution, getSimpleDate } from "../../lib/client-attribution";
import { getUtmParam, trackableFields } from "../web-form";
import { ChannelsButtons } from "../sections";
import { Slack } from "../icon/slack";
import { SectionContext } from "../sections/context";
import { FormLabel } from "../sections/demo-ux";

import * as styles from "./email-signup.module.css";
import * as stylesWebForm from "../web-form-field.module.css";

const postUrl = "/.netlify/functions/sendgrid-forms";
const isBrowser = typeof window !== "undefined";

const slugFromUrl = (href = isBrowser ? window.location.pathname : "") => {
  if (!href) return "";
  const url = href.replace(/\/$/, "");
  return url.substring(url.lastIndexOf("/") + 1);
};

// TEMP DURING PHASED ROLLOUT
const SUBSCRIBE_ID = "email-signup";

export const EmailSignup = ({
  buttonText,
  buttonClasses,
  autoFocus,
  attribution,
  supressMessage,
  toggle,
  brand,
  placeholder,
  domId = SUBSCRIBE_ID,
  showChannels,
  buttonIsSmall,
  inputClasses,
  email, // poorly named prop for form data
  autoSubmitEmail, // email address
  wrapperClasses,
  hubspotSubmit,
  extraFormFields,
  baseFormFields,
}) => {
  const fields = [{ name: "email", value: autoSubmitEmail || "" }]
    .concat(
      baseFormFields
        ? [{ name: "firstname", value: "" }, { name: "lastname", value: "" }]
        : []
    )
    .concat(extraFormFields ? [{ name: "jobtitle", value: "" }] : []);
  const hiddenFields = []
    .concat(
      domId === SUBSCRIBE_ID ? [{ name: "lists", value: "uncommon" }] : []
    )
    .concat(email ? [
      { name: "event_title__most_recent_", value: email?.title },
      { name: "offer_page_url__most_recent_", value: email?.pageUrl },
      { name: "event_url__most_recent_", value: email?.url },
    ] : [])
    .concat(email?.statusFields || [])
    .concat([
      { name: "source", value: attribution?.source },
      { name: "medium" },
      {
        name: "campaign",
        value: attribution?.campaign,
      },
      {
        name: "content",
        value: attribution?.content,
      },
      {
        name: "campaign_type",
        value: attribution?.campaignType,
      },
      {
        name: "page_cta_path_on_website",
        value: attribution?.website_attribution,
      },
      // {
      //   name: "first_seen_on_website",
      //   storage: (storage) => getSimpleDate("origin.created", storage),
      // },
      {
        name: "utm_parameter___created_date",
        storage: (storage) =>
          getSimpleDate(
            `urlParams.${trackableFields.find((f) =>
              getStashed({ id: `urlParams.${f}`, storage })
            )}.created`,
            storage
          ),
      },
      {
        name: "utm_parameter___created_date_original",
        storage: (storage) =>
          getSimpleDate(
            `origin.urlParams.${trackableFields.find((f) =>
              storage.getItem(`origin.urlParams.${f}`)
            )}.created`,
            storage
          ),
      },
    ])
    .concat(
      trackableFields.reduce((prev, id) => {
        return prev.concat([
          { name: `utm_parameter___${id}`, param: `utm_${id}` },
          {
            name: `utm_parameter___${id}_original`,
            storage: (storage) => storage.getItem(`origin.urlParams.${id}`),
          },
        ]);
      }, [])
    );

  const [initVals, setInitVales] = useState(
    fields.concat(hiddenFields).reduce(
      (obj, field) =>
        Object.assign({}, obj, {
          [field.name]: field.value || "",
        }),
      {}
    )
  );

  const [successTrigger, setSuccessTrigger] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const site = useContext(SiteContext);
  const section = useContext(SectionContext)
  const formikRef = useRef();

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    // console.debug("POST VALUES", values);
    setSubmitting(false);
    site.metrics.logClick("header-widget--join-success");
    setSuccessTrigger(true);
    site.metrics.analytics.storage.setItem("user.email", values.email);
    if (toggle) toggle(values.email);
    resetForm();

    if (hubspotSubmit) {
      if (window._hsq) {
        window._hsq.push(["identify", { email: values.email }]);
      }
      const hutk = document.cookie.split(";").find((c) => c.trim().startsWith("hubspotutk="))?.split("=")[1] || "";
      const { email: emailVal, ...attributionFields } = values;
      fetch("/.netlify/functions/hubspot-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formId: domId,
          hutk,
          pageUri: window.location.href,
          pageName: document.title,
          fields: { email: emailVal, ...attributionFields },
        }),
      }).catch((err) => console.error("HubSpot fallback submit failed:", err));
    }

    // try {
    //   const res = await fetch(postUrl, {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //     },
    //     body: JSON.stringify(values),
    //   });
    //   if (!res.ok) {
    //     site.metrics.logError("post-response-fail", res);
    //   } else {
    //     console.debug("POST SUCCESS", res);
    //     site.metrics.logClick("header-widget--join-success");
    //   }
    // } catch (e) {
    //   site.metrics.logError("post-try-catch", e);
    // } finally {
    //   setSubmitting(false);
    //   setSuccessTrigger(true);
    //   toggle();
    //   resetForm();
    // }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.email) {
      errors.email = "Required";
    } else if (!isEmail(values.email)) {
      errors.email = "Invalid email address";
    }
    return errors;
  };

  useEffect(() => {
    clientAttribution({
      isActive: site.isActive,
      storage: site.metrics.analytics.storage,
      referrer: document.referrer,
      callback: (urlParams) => {
        // make key value and combine
        setInitVales(
          fields.concat(hiddenFields).reduce(
            (obj, field) =>
              Object.assign({}, obj, {
                [field.name]: field.name === "email" && autoSubmitEmail
                  ? autoSubmitEmail
                  : field.storage
                    ? field.storage(site.metrics.analytics.storage) || ""
                    : field.param
                      ? urlParams.get(field.param) || ""
                      : getUtmParam({ id: field.name, urlParams }) ||
                      field.value ||
                      "",
              }),
            {}
          )
        );

        if (urlParams.has("show")) {
          setShowHidden(true);
        }
      },
    });
  }, [autoSubmitEmail]);

  useEffect(() => {
    if (!autoSubmitEmail) {
      return;
    }

    const attemptSubmit = () => {
      if (formikRef.current && formikRef.current.submitForm) {
        console.debug("Auto-submitted EmailSignup form with data:", autoSubmitEmail);

        // Try to trigger a real form submission event for HubSpot
        const formElement = document.getElementById(domId);
        if (formElement) {
          // Dispatch a submit event that HubSpot can listen to
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          formElement.dispatchEvent(submitEvent);
        }
        formikRef.current.submitForm();
      } else {
        console.debug("Formik ref not ready, retrying...", { formikRef: formikRef.current });
        setTimeout(attemptSubmit, 500);
      }
    };

    const timeoutId = setTimeout(attemptSubmit, 1000);

    return () => clearTimeout(timeoutId);
  }, [autoSubmitEmail, domId]);

  const fieldBoxClasses = cn(
    "flex items-center p-1 sm:p-1.5 rounded-xl",
    section?.isDarkBg ? "bg-light-10" : "bg-white",
    styles.appear,
    wrapperClasses
  );

  const inputFieldClasses = cn(
    "w-full text-sm border-transparent outline-none focus:ring-0 outline-none",
    "rounded-lg border-none px-2 md:px-4 py-2 shadow-none",
    styles.inputField,
    section?.isDarkBg && !site.isHomepage
      ? stylesWebForm.inputFieldDark
      : "bg-white text-black",
    inputClasses
  );

  // Matches the input styling used in the demo-ux form (demo-ux.js DemoForm).
  const demoFieldClasses = cn(
    "w-full p-2 rounded-lg transition-all duration-200",
    "bg-white text-dark-80 border-4 placeholder-dark-60",
    "outline-none focus:ring-0 border-transparent focus:border-green-emerald",
    inputClasses
  );

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={initVals}
        validate={validate}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, values }) => {
          const submitButton = (
            <Button
              text={buttonText || "Subscribe"}
              deactivated={isSubmitting}
              className={(baseFormFields || extraFormFields) ? cn(buttonClasses, "w-full") : buttonClasses}
              metrics={{
                action: attribution?.action || "uncommon",
                label: attribution?.label || `${attribution?.campaignType}--subscribe-attempt`,
                website_attribution: attribution?.website_attribution || slugFromUrl(),
              }}
              bgColor="bg-green-emerald"
              icon={domId === "join-slack" ? <Slack size={18} /> : null}
              isBig={buttonIsSmall ? null : true}
              isSmall={buttonIsSmall}
            />
          );

          return (
            <Form id={domId} data-hs-do-not-collect="true">
              <div
                className={cn(
                  "flex flex-col gap-4",
                  autoSubmitEmail ? "hidden" : null
                )}
              >
                {(baseFormFields || extraFormFields) ? (
                  <>
                    {baseFormFields && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <FormLabel
                            htmlFor="firstname"
                            focused={focusedField === "firstname"}
                            lowOpacity={true}
                            hasValue={!!values.firstname}
                          >
                            First name
                          </FormLabel>
                          <Field
                            type="text"
                            name="firstname"
                            className={demoFieldClasses}
                            placeholder="First name"
                            autoComplete="given-name"
                            onFocus={() => setFocusedField("firstname")}
                            onBlur={() => setFocusedField(null)}
                          />
                        </div>
                        <div>
                          <FormLabel
                            htmlFor="lastname"
                            focused={focusedField === "lastname"}
                            lowOpacity={true}
                            hasValue={!!values.lastname}
                          >
                            Last name
                          </FormLabel>
                          <Field
                            type="text"
                            name="lastname"
                            className={demoFieldClasses}
                            placeholder="Last name"
                            autoComplete="family-name"
                            onFocus={() => setFocusedField("lastname")}
                            onBlur={() => setFocusedField(null)}
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      <FormLabel
                        htmlFor="email"
                        focused={focusedField === "email"}
                        lowOpacity={true}
                        hasValue={!!values.email}
                      >
                        Email
                      </FormLabel>
                      <Field
                        type="email"
                        name="email"
                        className={demoFieldClasses}
                        placeholder={placeholder || "Work email"}
                        autoFocus={autoFocus}
                        onFocus={() => setFocusedField("email")}
                        onBlur={() => setFocusedField(null)}
                      />
                      <HiddenFields fields={hiddenFields} show={showHidden} />
                    </div>
                    {extraFormFields && (
                      <div>
                        <FormLabel
                          htmlFor="jobtitle"
                          focused={focusedField === "jobtitle"}
                          lowOpacity={true}
                          hasValue={!!values.jobtitle}
                        >
                          Job title
                        </FormLabel>
                        <Field
                          type="text"
                          name="jobtitle"
                          className={demoFieldClasses}
                          placeholder="Job title"
                          onFocus={() => setFocusedField("jobtitle")}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>
                    )}
                    <div className="mt-4 w-2/3 mx-auto">
                      {submitButton}
                    </div>
                  </>
                ) : (
                  <div className={fieldBoxClasses}>
                    <div className="flex-grow mr-2">
                      <Field
                        type="email"
                        name="email"
                        className={inputFieldClasses}
                        placeholder={placeholder || "Your email"}
                        autoFocus={autoFocus}
                      />
                      <HiddenFields fields={hiddenFields} show={showHidden} />
                      {/* <ErrorMessage
              name="email"
              component="p"
              className={cn(
                "absolute text-secondary mt-3",
                styles.errorMessage
              )}
            /> */}
                    </div>
                    {submitButton}
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      {showChannels && (
        <div className="mt-12">
          <p className="font-semibold mb-3">Or find us here:</p>
          <ChannelsButtons />
        </div>
      )}
      {successTrigger && !supressMessage && (
        <Dialog
          title="Thanks. Let's go!"
          message="We're glad to have you."
        />
      )}
    </>
  );
};
